import React, { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Hand } from 'lucide-react';

const QUERY = '(max-width: 767px)';
const HINT_KEY = 'realty237.swipe-hint.';

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

// Shows once per carousel, the first time it scrolls into view.
const useSwipeHint = (ref, testId, emblaApi) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!ref.current || localStorage.getItem(HINT_KEY + testId)) return undefined;
    let timer;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      localStorage.setItem(HINT_KEY + testId, '1');
      setShow(true);
      timer = setTimeout(() => setShow(false), 3500);
    }, { threshold: 0.6 });
    io.observe(ref.current);
    return () => { io.disconnect(); clearTimeout(timer); };
  }, [ref, testId]);
  useEffect(() => {
    if (!emblaApi || !show) return undefined;
    const hide = () => setShow(false);
    emblaApi.on('pointerDown', hide);
    return () => emblaApi.off('pointerDown', hide);
  }, [emblaApi, show]);
  return show;
};

const SwipeHint = ({ testId }) => (
  <div
    className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 z-10 flex items-center gap-2 rounded-full bg-stone-900/85 text-white text-xs font-semibold px-4 py-2 shadow-lg backdrop-blur-md animate-in fade-in zoom-in-95 duration-300"
    data-testid={`${testId}-swipe-hint`}
  >
    <Hand className="h-4 w-4 animate-swipe-nudge" />
    Glissez
  </div>
);

const Track = ({ slides, testId }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 4500, stopOnInteraction: false, stopOnMouseEnter: true })],
  );
  const [selected, setSelected] = useState(0);
  const wrapRef = useRef(null);
  const showHint = useSwipeHint(wrapRef, testId, emblaApi);

  const onSelect = useCallback(() => emblaApi && setSelected(emblaApi.selectedScrollSnap()), [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return undefined;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div data-testid={`${testId}-carousel`}>
      <div className="relative" ref={wrapRef}>
        <div className="overflow-hidden -mx-1 px-1" ref={emblaRef}>
          <div className="flex">
            {slides.map((slide, i) => (
              <div key={i} className="shrink-0 basis-[85%] pr-4 py-1" data-testid={`${testId}-slide-${i}`}>
                {slide}
              </div>
            ))}
          </div>
        </div>
        {showHint && <SwipeHint testId={testId} />}
      </div>
      <div className="flex justify-center gap-2 mt-5" data-testid={`${testId}-dots`}>
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Aller à l'élément ${i + 1}`}
            onClick={() => emblaApi && emblaApi.scrollTo(i)}
            className={`h-2 rounded-full transition-[width,background-color] duration-300 ${i === selected ? 'w-6 bg-orange-500' : 'w-2 bg-stone-300'}`}
            data-testid={`${testId}-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
};

// Phone: swipeable autoplay carousel with dots. Tablet/desktop: the original grid.
export const MobileCarousel = ({ slides, desktop, testId, below = null }) => {
  const isMobile = useIsMobile();
  return isMobile ? <><Track slides={slides} testId={testId} />{below}</> : desktop;
};

export default MobileCarousel;
